import { useState, type ReactNode } from 'react';
import { Avatar, MasterDetailShell, PaneEmpty, RailSearch, RailRecents, hl, useUnsavedGuard } from '../common';
import { UserPane } from './UserPane';
import { UserActivityRail } from './UserActivityRail';
import type { User } from './types';

export interface AccountModalProps {
  users: User[];
  selectedEmail: string | null; // null → open in "add someone" mode
  myEmail: string;
  onClose: () => void;
  onChanged: () => void;
}

// The account master-detail modal — the twin of StaffModal / OwnerBookingModal.
// Same MasterDetailShell + RailSearch, so you navigate between people in the rail
// and edit the selected one in the pane, without closing. When a user is
// selected the rail shows THEIR activity timeline (the booking modal's pattern,
// fed by /activity/feed?actor=); in add mode it falls back to the recent-people
// list.
export function AccountModal({ users, selectedEmail, myEmail, onClose, onChanged }: AccountModalProps) {
  const seed = selectedEmail ? users.find((u) => u.email === selectedEmail) ?? null : null;
  const [selected, setSelected] = useState<User | null>(seed);
  const [creating, setCreating] = useState(selectedEmail === null);
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);

  // Save / remove closes the modal and refreshes the page list (same as the
  // staff and booking modals).
  const afterSave = () => { onChanged(); onClose(); };
  // THROUGH THE GUARD, like every other way out. The pane is keyed per person,
  // so picking another one unmounts it — and an edited email or role went
  // with it, unasked (review on #239).
  const { guard } = useUnsavedGuard();
  const selectUser = (u: User) => guard(() => { setSelected(u); setCreating(false); setQuery(''); setFocused(false); });
  const startCreate = () => guard(() => { setCreating(true); setSelected(null); setQuery(''); setFocused(false); });

  const candidates =
    query.trim() || focused
      ? users.filter(
          (u) =>
            !query.trim() ||
            u.email.toLowerCase().includes(query.toLowerCase()) ||
            u.role.toLowerCase().includes(query.toLowerCase()),
        )
      : [];

  // In ADD mode (no user selected) the rail standing content is the recent-people
  // list. Users have no updated-at, so "recent" = most recently added.
  const recent = [...users]
    .sort((a, b) => (b.addedAt ?? '').localeCompare(a.addedAt ?? ''))
    .slice(0, 10);

  const currentCard: ReactNode = selected && !creating && (
    <div className="curowner active">
      <Avatar name={selected.email} id={selected.email} size={32} />
      <div className="rail-rowtext">
        <div className="nm">{selected.email}</div>
        <div className="mt">{selected.role}</div>
      </div>
    </div>
  );

  const rail = (
    <RailSearch<User>
      placeholder="Search people…"
      query={query}
      setQuery={setQuery}
      focused={focused}
      setFocused={setFocused}
      items={candidates}
      idOf={(u) => u.email}
      isCurrent={(u) => u.email === selected?.email}
      onPick={selectUser}
      restLabel="People"
      addLabel="Add someone"
      onAdd={startCreate}
      current={currentCard}
      noMatchText="No one matches"
      showOnFocus={false}
      renderRow={(u, q) => (
        <>
          <Avatar name={u.email} id={u.email} size={30} />
          <div className="rail-rowtext">
            <div className="nm">{hl(u.email, q)}</div>
            <div className="mt">{hl(u.role, q)}</div>
          </div>
          {u.email === selected?.email ? <span className="curtick" aria-label="Current" title="Current">●</span> : <div className="rt">{u.role}</div>}
        </>
      )}
    />
  );

  // "Recent people" list (add mode). Shared RailRecents — same on every modal.
  const recentList = (
    <RailRecents<User>
      items={recent}
      idOf={(u) => u.email}
      isCurrent={(u) => u.email === selected?.email}
      onPick={selectUser}
      renderRow={(u) => (
        <>
          <Avatar name={u.email} id={u.email} size={30} />
          <div className="rail-rowtext">
            <div className="nm">{u.email}</div>
            <div className="mt">{u.role}</div>
          </div>
          {u.email === selected?.email && <span className="curtick" aria-label="Current" title="Current">●</span>}
        </>
      )}
    />
  );

  // The rail's standing content below the search: the selected user's activity
  // timeline (booking-modal pattern), or — in add mode — the recent-people list.
  const railStanding = selected && !creating ? <UserActivityRail email={selected.email} /> : recentList;

  const pane =
    creating || selected ? (
      <UserPane
        key={creating ? 'new' : selected!.email}
        user={creating ? null : selected}
        myEmail={myEmail}
        onSaved={afterSave}
        onClose={onClose}
        onRemoved={afterSave}
      />
    ) : (
      <PaneEmpty icon="user" title="No one selected" hint="Pick a person from the list, or add someone." />
    );

  return (
    <MasterDetailShell
      className="account-obx"
      title={creating ? 'Add someone' : selected?.email ?? 'Access'}
      modeLabel={creating ? 'new' : 'access'}
      onClose={onClose}
      rail={<>{rail}{railStanding}</>}
    >
      {pane}
    </MasterDetailShell>
  );
}
